import type { CSSProperties } from 'react';
import { colorWithAlpha } from '@/utils/colorWithAlpha';

export { ProjectCard } from './ProjectCard';
export { ProjectList } from './ProjectList';
export { ProjectModal } from './ProjectModal';

import { IProject, IProjectUser, Project } from '@/types/Project.model';

/**
 * Get the current user's role in a project
 * @param project - The project object
 * @param userId - The current user's ID
 * @returns The user's role or 'Member' as default
 */
export const getCurrentUserRole = (project: IProject, userId: string): string => {
  const projectUser = project.projectUsers.find((u) => u.userId === userId);
  return projectUser ? projectUser.role : 'Member';
};

/**
 * Get the badge color for a role
 * @param role - The user's role
 * @param corporateColor - The corporate color for admin roles
 * @returns The badge color
 */
export const getRoleBadgeColor = (role: string, corporateColor: string): string => {
  return role === Project.UserRole.ADMIN ? corporateColor : 'blue';
};

/** Soft pill styles for project roles — shared by ProjectCard and ProjectModal */
export const getRolePillStyles = (
  role: string,
  corporateColor: string
): CSSProperties => {
  if (role === Project.UserRole.ADMIN) {
    return {
      backgroundColor: colorWithAlpha(corporateColor, 0.12),
      color: corporateColor,
      border: `1px solid ${colorWithAlpha(corporateColor, 0.22)}`,
    };
  }
  if (role === Project.UserRole.EDITOR) {
    return {
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      color: '#2563eb',
      border: '1px solid rgba(59, 130, 246, 0.2)',
    };
  }
  return {
    backgroundColor: 'rgba(100, 116, 139, 0.08)',
    color: '#64748b',
    border: '1px solid rgba(100, 116, 139, 0.16)',
  };
};
